/**
 * Turning a card into something a person can take away.
 *
 * Kept free of React and of echarts so both halves are testable: the CSV is
 * pure text, and the PNG is produced by the chart instance itself, which this
 * module only names.
 */

/**
 * RFC 4180 quoting. A field is quoted when it holds a delimiter, a quote or a
 * newline, and an embedded quote is doubled. A leading =, +, - or @ is also
 * quoted and prefixed, because a spreadsheet treats those as formulas and a
 * value like "-1" from a real result set would otherwise execute on open.
 */
function csvField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  // JSON retains structured fields and array boundaries inside a CSV cell.
  const text =
    value instanceof Date ? value.toISOString() : typeof value === 'object' ? JSON.stringify(value) : String(value);
  const risky = /^[=+\-@\t\r]/.test(text);
  const body = risky ? `'${text}` : text;
  return /[",\n\r]/.test(body) || risky ? `"${body.replace(/"/g, '""')}"` : body;
}

export function toCsv(rows: Array<Record<string, unknown>>, columns: Array<string>): string {
  const header = columns.map(csvField).join(',');
  const body = rows.map((row) => columns.map((column) => csvField(row[column])).join(','));
  // Excel needs CRLF to treat an embedded newline inside a quoted field as data.
  return [header, ...body].join('\r\n');
}

/** A filename that survives a filesystem and still says which card it came from. */
export function exportFileName(title: string, extension: string, now: Date = new Date()): string {
  const slug =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'lens-card';
  return `${slug}-${now.toISOString().slice(0, 10)}.${extension}`;
}

/**
 * Hands the browser a file. Revoking on the next frame rather than immediately
 * matters: Safari has not started reading the blob when the click returns.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

export function downloadCsv(rows: Array<Record<string, unknown>>, columns: Array<string>, title: string): void {
  // The BOM is what makes Excel read the file as UTF-8 rather than as the
  // local codepage, which otherwise mangles every accented vendor name.
  const blob = new Blob(['﻿', toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, exportFileName(title, 'csv'));
}

export function downloadDataUrl(dataUrl: string, title: string, extension = 'png'): void {
  const [meta, encoded] = dataUrl.split(',');
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const type = meta.match(/data:([^;]+)/)?.[1] ?? 'image/png';
  downloadBlob(new Blob([bytes], { type }), exportFileName(title, extension));
}
